/**
 * Per-channel voice-playback rate, persisted locally.
 *
 * Signal lets the user toggle 1x / 1.5x / 2x playback for voice
 * messages and remembers the choice per conversation. The state is
 * strictly local — never synced over the wire — because a peer cannot
 * usefully influence how *we* hear *their* recording, and any sync
 * would be a needless side-channel telling the server which threads we
 * listen to fast.
 *
 * At-rest posture mirrors the iOS `VoicePlaybackRateStore`: per-channel
 * records persisted in IndexedDB (analogous to iOS's Documents/voice-rate/
 * directory with `.completeFileProtection`).
 *
 * Pure-logic + IndexedDB ONLY: no MediaRecorder, no AudioContext,
 * no Web Audio API imports.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/VoiceMessagePlaybackRatePolicy.swift`.
 */

// ---------------------------------------------------------------------------
// Rate enum
// ---------------------------------------------------------------------------

export type VoicePlaybackRate = 1.0 | 1.5 | 2.0;

export const VOICE_PLAYBACK_RATES: readonly VoicePlaybackRate[] = [1.0, 1.5, 2.0];

export function isVoicePlaybackRate(v: unknown): v is VoicePlaybackRate {
  return v === 1.0 || v === 1.5 || v === 2.0;
}

// ---------------------------------------------------------------------------
// Record stored in IndexedDB
// ---------------------------------------------------------------------------

export interface VoicePlaybackRecord {
  readonly channelId: string;
  readonly rate: VoicePlaybackRate;
}

// ---------------------------------------------------------------------------
// IndexedDB plumbing
// ---------------------------------------------------------------------------

const DB_VERSION = 1;
const STORE_NAME = "voice-rate";

function openDb(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "channelId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(
  db: IDBDatabase,
  channelId: string,
): Promise<VoicePlaybackRecord | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(channelId);
    req.onsuccess = () => resolve(req.result as VoicePlaybackRecord | undefined);
    req.onerror = () => reject(req.error);
  });
}

async function idbPut(
  db: IDBDatabase,
  record: VoicePlaybackRecord,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const req = tx.objectStore(STORE_NAME).put(record);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

async function idbClear(db: IDBDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const req = tx.objectStore(STORE_NAME).clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// ---------------------------------------------------------------------------
// VoicePlaybackRateStore
// ---------------------------------------------------------------------------

const DEFAULT_DB_NAME = "kaether:voice-rate:v1";

/**
 * IndexedDB-backed per-channel playback rate store.
 *
 * All methods are async because every read/write goes through IndexedDB.
 * Instantiate with a unique `dbName` in tests for isolation. The
 * `sharedVoicePlaybackRateStore` singleton is for production use.
 *
 * Mirrors iOS `VoicePlaybackRateStore`.
 */
export class VoicePlaybackRateStore {
  private readonly dbName: string;
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor(dbName: string = DEFAULT_DB_NAME) {
    this.dbName = dbName;
  }

  private db(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = openDb(this.dbName);
    }
    return this.dbPromise;
  }

  /**
   * Returns the user's chosen rate for this channel, or 1.0 (x1) if no
   * preference has been recorded yet.
   */
  async getRate(channelId: string): Promise<VoicePlaybackRate> {
    const db = await this.db();
    const record = await idbGet(db, channelId);
    if (record === undefined) return 1.0;
    return isVoicePlaybackRate(record.rate) ? record.rate : 1.0;
  }

  /**
   * Persist the user's playback rate choice for `channelId`.
   */
  async setRate(channelId: string, rate: VoicePlaybackRate): Promise<void> {
    const db = await this.db();
    await idbPut(db, { channelId, rate });
  }

  /**
   * Clear all stored rates. Used in tests and on sign-out.
   */
  async resetAll(): Promise<void> {
    const db = await this.db();
    await idbClear(db);
  }
}

// ---------------------------------------------------------------------------
// Shared singleton (production use)
// ---------------------------------------------------------------------------

/**
 * Shared singleton for production code.
 * Tests must instantiate their own `VoicePlaybackRateStore` with a unique
 * `dbName` for isolation.
 */
export const sharedVoicePlaybackRateStore = new VoicePlaybackRateStore();
